import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useState, useContext } from "react";
import AngledAxisTick from '../AngledAxisTick';
import calculateTotalGrowth from '../../../helpers/dataHelpers/calculateTotalGrowth';
import growthPerRow from "../../../helpers/dataHelpers/growthPerRow";
import ScoreBlock from '../../analysis/ScoreBlock';
import AnalyticsEmptyState from "../../dashboard/AnalyticsEmptyState";
import LiveReportRenderContext from '../../../StateManagment/LiveReportRenderContext';
import { CHART_MARK_STROKE, CHART_MARK_STROKE_WIDTH } from '../../../helpers/chartPalette';

const MatrixDevelopmentBarChart = ({ data, lowerScale, upperScale, totalResponses, effectId, benchmark, benchmarkLoading, benchmarkUnavailableReason, infoPopoverContent, graphColor }) => {
  const [hoveredBar, setHoveredBar] = useState(null);
  // In a live report, "totale groei" is its own toggle option, so the heading
  // above the chart is suppressed. Dashboard/analysis keep it.
  const inLiveReport = useContext(LiveReportRenderContext);

  // Ensure data is not empty before processing
  if (!data || data.length === 0) {
    return <AnalyticsEmptyState />;
  }

  // Extract unique keys for dynamic data (assuming every data entry has the same keys)
  const uniqueRowTitles = Object.keys(data[0]).filter((key) => key !== "name");

  // Calculate total growth normalized to the scale (unchanged)
  const totalGrowth = calculateTotalGrowth(data, uniqueRowTitles, upperScale, lowerScale).toFixed(1);

  // Calculate growth for each row title between moments, normalized to the scale
  const growthDataForRows = growthPerRow(data, uniqueRowTitles, upperScale, lowerScale);

  // --- Customized Legend ---
  // Check the original data (assuming consistency across moments) for the negative flag.
  const CustomizedLegend = ({ payload }) => (
    <ul>
      {payload.map((entry, index) => {
        const rowTitle = entry.dataKey;
        // Get negative flag from the first data element (if available)
        const isNegative = data && data.length > 0 && data[0][rowTitle]?.negative;
        const growthArray = growthDataForRows[rowTitle];
        const lastGrowth = growthArray ? growthArray[growthArray.length - 1] : null;
        const growthLabel = lastGrowth !== null ? ` (${lastGrowth > 0 ? "+" : ""}${lastGrowth}%)` : "";
        // Append (negatief) if the question is negative
        const titleDisplay = isNegative ? `${rowTitle} (omgekeerd)` : rowTitle;
        
        return (
          <li
            key={`item-${index}`}
            onMouseEnter={() => setHoveredBar(rowTitle)}
            onMouseLeave={() => setHoveredBar(null)}
            style={{
              cursor: "pointer",
              fontWeight: hoveredBar === rowTitle ? "bold" : "normal",
              color: entry.color,
            }}
          >
            {titleDisplay}{growthLabel}
          </li>
        );
      })}
    </ul>
  );

  // --- Custom Tooltip ---
  // Use the original data to check for negative formulations.
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // Identify the corresponding moment from the original data based on the label
      const momentData = data.find(item => item.name === label);
      return (
        <div
          className="custom-tooltip"
          style={{
            background: "#fff",
            border: "1px solid #ccc",
            padding: "10px"
          }}
        >
          <p>{label}</p>
          {payload.map((entry, index) => {
            const rowKey = entry.dataKey;
            // Retrieve the original value and negative flag
            const originalValue = momentData ? momentData[rowKey]?.value : entry.value;
            const isNegative = momentData ? momentData[rowKey]?.negative : false;
            return (
              <div key={`tooltip-${index}`} style={{ color: entry.color }}>
                <span>
                  {rowKey} {isNegative ? "(omgekeerd)" : ""} : {originalValue.toFixed(1)}
                </span>
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  };

  return (
    <>
      {!inLiveReport && <ScoreBlock totalGrowth={Number(totalGrowth)} benchmark={benchmark} benchmarkLoading={benchmarkLoading} benchmarkUnavailableReason={benchmarkUnavailableReason} effectId={effectId} />}
      <ResponsiveContainer width="100%" height={500}>
        <BarChart
          // Here we transform the data so the chart’s numbers are adjusted
          data={data.map(item => {
            const o = { name: item.name };
            Object.entries(item).forEach(([k, v]) => {
              if (k !== 'name') o[k] = v?.value ?? null; // no mirroring here
            });
            return o;
          })}
          margin={{
            top: 5,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <XAxis
          dataKey="name"
          tick={<AngledAxisTick fill={graphColor || undefined} />}
          interval={0}
          height={70}
          axisLine={false}
          tickLine={false}
          stroke={graphColor || 'black'}
          />
          <YAxis
          domain={[0, upperScale]}
          axisLine={false}
          tickLine={false}
          stroke={graphColor || 'black'}
          tick={{ fill: graphColor || undefined }}
          />
          {/* Use the custom tooltip here */}
          <Tooltip content={<CustomTooltip />} />
          {/* <Legend content={<CustomizedLegend />} verticalAlign="bottom" /> */}
          {uniqueRowTitles.map((rowTitle, index) => (
            <Bar
              key={`bar-${index}`}
              dataKey={rowTitle}
              fill={data[0][rowTitle]?.color || "#206c7f"}
              fillOpacity={hoveredBar && hoveredBar !== rowTitle ? 0.3 : 1}
           stroke={CHART_MARK_STROKE} strokeWidth={CHART_MARK_STROKE_WIDTH} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </>
  );
};

export default MatrixDevelopmentBarChart;
